// Pure (tiptap-free) Youtube URL helpers, extracted from Youtube.ts so the main
// barrel can re-export them without dragging @tiptap/core into the eager bundle.
// The tiptap extension definition (`Youtube`) stays in Youtube.ts.

export const YOUTUBE_PASTE_REGEX_GLOBAL = /https?:\/\/(?:www\.|m\.|music\.)?(?:youtu\.be\/[^\s]+|youtube(?:-nocookie)?\.com\/(?:watch\?[^\s]*v=[A-Za-z0-9_-]{6,}[^\s]*|embed\/[A-Za-z0-9_-]{6,}[^\s]*|shorts\/[A-Za-z0-9_-]{6,}[^\s]*|live\/[A-Za-z0-9_-]{6,}[^\s]*))/gi
const YOUTUBE_VIDEO_ID_REGEX = /^[A-Za-z0-9_-]{6,}$/
const YOUTUBE_WATCH_HOSTNAMES = ['youtube.com', 'www.youtube.com', 'm.youtube.com', 'music.youtube.com']
const YOUTUBE_SHORT_HOSTNAMES = ['youtu.be', 'www.youtu.be']
const YOUTUBE_EMBED_HOSTNAMES = [...YOUTUBE_WATCH_HOSTNAMES, 'youtube-nocookie.com', 'www.youtube-nocookie.com']
const YOUTUBE_EMBED_PASSTHROUGH_PARAMS = new Set([
  'autoplay',
  'controls',
  'end',
  'index',
  'list',
  'loop',
  'mute',
  'playlist',
  'rel',
  'start',
  'start_radio',
  't',
])

function isYoutubePlaylistParam(key: string): boolean {
  return key === 'list' || key === 'playlist'
}

export type YoutubeEmbedOptions = {
  url: string
  nocookie?: boolean
  allowFullscreen?: boolean
  autoplay?: boolean
  controls?: boolean
  modestBranding?: boolean
  rel?: number
  startAt?: number
}

export type SetYoutubeVideoOptions = { src: string; width?: number | string; height?: number | string; start?: number }

export type YoutubeUrlMetadata = {
  normalizedUrl: string
  videoId: string
  passthroughParams: URLSearchParams
  startAt: number | null
}

function parseYoutubeTimeValue(value: string | null | undefined): number | null {
  if (!value) {
    return null
  }

  const trimmedValue = value.trim()
  if (!trimmedValue) {
    return null
  }

  if (/^\d+$/.test(trimmedValue)) {
    return Number(trimmedValue)
  }

  const timeMatch = trimmedValue.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/i)
  if (!timeMatch) {
    return null
  }

  const hours = Number(timeMatch[1] ?? 0)
  const minutes = Number(timeMatch[2] ?? 0)
  const seconds = Number(timeMatch[3] ?? 0)
  const totalSeconds = hours * 3600 + minutes * 60 + seconds

  return totalSeconds > 0 ? totalSeconds : null
}

function getYoutubeHashParams(hash: string): URLSearchParams {
  const normalizedHash = hash.replace(/^#/, '').trim()
  if (!normalizedHash) {
    return new URLSearchParams()
  }

  return normalizedHash.includes('=')
    ? new URLSearchParams(normalizedHash)
    : new URLSearchParams([['t', normalizedHash]])
}

function extractYoutubeVideoId(parsedUrl: URL): string | null {
  const hostname = parsedUrl.hostname.toLowerCase()

  if (YOUTUBE_SHORT_HOSTNAMES.includes(hostname)) {
    const shortVideoId = parsedUrl.pathname.replace(/^\/+|\/+$/g, '').split('/')[0] ?? ''
    return YOUTUBE_VIDEO_ID_REGEX.test(shortVideoId) ? shortVideoId : null
  }

  if (YOUTUBE_WATCH_HOSTNAMES.includes(hostname) && parsedUrl.pathname === '/watch') {
    const watchVideoId = parsedUrl.searchParams.get('v')
    return watchVideoId && YOUTUBE_VIDEO_ID_REGEX.test(watchVideoId) ? watchVideoId : null
  }

  if (!YOUTUBE_EMBED_HOSTNAMES.includes(hostname)) {
    return null
  }

  const embeddedVideoId = parsedUrl.pathname.match(/^\/(?:embed|shorts|live)\/([^/?#]+)/)?.[1] ?? null
  return embeddedVideoId && YOUTUBE_VIDEO_ID_REGEX.test(embeddedVideoId) ? embeddedVideoId : null
}

export function getYoutubeUrlMetadata(url: string): YoutubeUrlMetadata | null {
  try {
    const parsedUrl = new URL(url.trim())
    const videoId = extractYoutubeVideoId(parsedUrl)
    if (!videoId) {
      return null
    }

    const normalizedUrl = new URL('https://www.youtube.com/watch')
    normalizedUrl.searchParams.set('v', videoId)

    for (const [key, value] of parsedUrl.searchParams.entries()) {
      if (key === 'v' || isYoutubePlaylistParam(key) || !YOUTUBE_EMBED_PASSTHROUGH_PARAMS.has(key)) {
        continue
      }

      normalizedUrl.searchParams.append(key, value)
    }

    const hashParams = getYoutubeHashParams(parsedUrl.hash)
    if (!normalizedUrl.searchParams.has('start') && !normalizedUrl.searchParams.has('t')) {
      const hashTimeValue = hashParams.get('start') ?? hashParams.get('t')
      if (hashTimeValue) {
        normalizedUrl.searchParams.set('t', hashTimeValue)
      }
    }

    const passthroughParams = new URLSearchParams(normalizedUrl.search)
    passthroughParams.delete('v')

    return {
      normalizedUrl: normalizedUrl.toString(),
      videoId,
      passthroughParams,
      startAt: parseYoutubeTimeValue(passthroughParams.get('start') ?? passthroughParams.get('t')),
    }
  } catch {
    return null
  }
}

export function normalizeYoutubeUrl(url: string): string {
  const trimmedUrl = url.trim()
  return getYoutubeUrlMetadata(trimmedUrl)?.normalizedUrl ?? trimmedUrl
}

export function buildYoutubeEmbedUrl(options: YoutubeEmbedOptions): string | null {
  const metadata = getYoutubeUrlMetadata(options.url)
  if (!metadata) {
    return null
  }

  const baseUrl = options.nocookie
    ? `https://www.youtube-nocookie.com/embed/${metadata.videoId}`
    : `https://www.youtube.com/embed/${metadata.videoId}`

  const params = new URLSearchParams(metadata.passthroughParams.toString())
  params.delete('t')

  if (options.allowFullscreen === false) {
    params.set('fs', '0')
  }

  if (options.autoplay) {
    params.set('autoplay', '1')
  }

  if (options.controls === false) {
    params.set('controls', '0')
  }

  if (options.modestBranding) {
    params.set('modestbranding', '1')
  }

  const startAt = typeof options.startAt === 'number' && options.startAt > 0
    ? options.startAt
    : metadata.startAt

  if (typeof startAt === 'number' && startAt > 0) {
    params.set('start', String(startAt))
  }

  if (typeof options.rel === 'number') {
    params.set('rel', String(options.rel))
  }

  const queryString = params.toString()
  return queryString ? `${baseUrl}?${queryString}` : baseUrl
}
